import html2canvas from 'html2canvas';

function sanitizeUnsupportedColorFunctions(cssText = '') {
  if (!cssText) return cssText;

  let result = cssText;
  
  let prevResult = '';

  while (result !== prevResult) {
    prevResult = result;
    result = result
      .replace(/color-mix\([^()]*(?:\([^()]*\)[^()]*)*\)/gi, 'rgba(0, 0, 0, 0.24)')
      .replace(/(?<!-)color\([^()]*(?:\([^()]*\)[^()]*)*\)/gi, 'rgb(0, 0, 0)') // negative lookbehind to avoid matching background-color, border-color, etc.
      .replace(/oklch\([^()]*(?:\([^()]*\)[^()]*)*\)/gi, 'rgb(0, 0, 0)')
      .replace(/oklab\([^()]*(?:\([^()]*\)[^()]*)*\)/gi, 'rgb(0, 0, 0)')
      .replace(/lch\([^()]*(?:\([^()]*\)[^()]*)*\)/gi, 'rgb(0, 0, 0)')
      .replace(/lab\([^()]*(?:\([^()]*\)[^()]*)*\)/gi, 'rgb(0, 0, 0)');
  }

  return result;
}

function hasUnsupportedColorFunction(value = '') {
  if (!value) return false;
  return /(?:^|[\s,:(])(?:color-mix|oklch|oklab|lch|lab)\s*\(|(?:^|[\s,:(])color\s*\(/i.test(value);
}


export async function captureElement(element, options = {}) {
  if (!element) {
    throw new Error('Element is required for screenshot capture');
  }

  const defaultOptions = {
    scale: 3, // Higher default scale for better quality
    backgroundColor: '#ffffff',
    logging: false,
    useCORS: true,
    allowTaint: true,
    foreignObjectRendering: false,
    imageTimeout: 15000, // Longer timeout for images to load
    ignoreElements: (el) => {
      return el.tagName === 'IFRAME' || el.tagName === 'SCRIPT';
    },
    onclone: (clonedDoc) => {
      const clonedWindow = clonedDoc.defaultView;

      const modals = clonedDoc.querySelectorAll('.ci-modal, .ci-modal-wide');
      modals.forEach((modal) => {
        modal.style.setProperty('width', '1400px', 'important');
        modal.style.setProperty('min-width', '1400px', 'important');
        modal.style.setProperty('max-width', '1400px', 'important');
        modal.style.setProperty('max-height', 'none', 'important');
        modal.style.setProperty('min-height', '0', 'important');
        modal.style.setProperty('height', 'auto', 'important');
        modal.style.setProperty('overflow', 'visible', 'important');
        modal.style.setProperty('overflow-y', 'visible', 'important');
        modal.style.setProperty('overflow-x', 'visible', 'important');
        modal.style.setProperty('display', 'flex', 'important');
        modal.style.setProperty('flex-direction', 'column', 'important');
      });

      const modalContents = clonedDoc.querySelectorAll(
        '.ci-modal-content, .ci-modal-table-content, .ci-comment-panel-content, ' +
        '.ci-comment-bank-modal .ci-modal-content, .ci-success-criteria-modal .ci-modal-content, ' +
        '.ci-modal-wide .ci-modal-table-content, .ci-modal-body, .ci-modal-inner'
      );
      modalContents.forEach((content) => {
        content.style.setProperty('max-height', 'none', 'important');
        content.style.setProperty('min-height', '0', 'important');
        content.style.setProperty('overflow', 'visible', 'important');
        content.style.setProperty('overflow-y', 'visible', 'important');
        content.style.setProperty('overflow-x', 'visible', 'important');
        content.style.setProperty('height', 'auto', 'important');
        content.style.setProperty('flex', 'none', 'important');
        content.style.setProperty('width', '100%', 'important');
      });

      const exportMenus = clonedDoc.querySelectorAll(
        '[data-export-format-menu="true"], [role="menu"][aria-label="Export format"]'
      );
      exportMenus.forEach((menu) => {
        menu.style.setProperty('display', 'none', 'important');
        menu.style.setProperty('visibility', 'hidden', 'important');
      });

      // Convert filter dropdowns to plain text for PDF
      const modalFilterGrids = clonedDoc.querySelectorAll('.ci-modal-filters-grid');
      modalFilterGrids.forEach((grid) => {
        const filterGroups = grid.querySelectorAll('.ci-modal-filter-group');
        filterGroups.forEach((group) => {
          const label = group.querySelector('label');
          const combobox = group.querySelector('[role="combobox"]');
          const datePicker = group.querySelector('.MuiBox-root');

          if (label && (combobox || datePicker)) {
            let displayText = '';

            // Extract display text
            if (combobox) {
              const fullText = combobox.textContent?.trim() || '';

              // Check if it has "+X more" pattern indicating multiple selections
              if (/\+\d+\s*more/.test(fullText)) {
                // Try to find the actual dropdown menu to get all selections
                const dropdownId = combobox.getAttribute('aria-controls');
                if (dropdownId) {
                  const menu = clonedDoc.getElementById(dropdownId);
                  if (menu) {
                    // Find all checked options
                    const checkedOptions = menu.querySelectorAll('input[type="checkbox"]:checked');
                    if (checkedOptions.length > 0) {
                      const allSelections = Array.from(checkedOptions).map(checkbox => {
                        const label = checkbox.closest('label') || checkbox.parentElement;
                        // Get text content excluding the checkbox itself
                        const clone = label.cloneNode(true);
                        clone.querySelector('input')?.remove();
                        clone.querySelectorAll('svg')?.forEach(svg => svg.remove());
                        return clone.textContent?.trim();
                      }).filter(Boolean);

                      if (allSelections.length > 0) {
                        displayText = allSelections.join(', ');
                      }
                    }
                  }
                }
              }

              // Fallback to the displayed text if we couldn't get all selections
              if (!displayText) {
                displayText = fullText.replace(/\+\d+\s*more/g, '').trim() || 'All';
              }
            } else if (datePicker) {
              // Extract just the date text, no icons
              const dateText = datePicker.textContent?.trim() || '';
              displayText = dateText;
            }

            // Hide all interactive elements including borders/boxes
            const toHide = group.querySelectorAll(
              '[role="combobox"], .MuiBox-root, button, svg, img, .MuiIconButton-root'
            );
            toHide.forEach((el) => {
              el.style.setProperty('display', 'none', 'important');
            });

            // Create plain text element
            const textDiv = clonedDoc.createElement('div');
            textDiv.textContent = displayText;
            textDiv.style.cssText = 'font-size: 14px; color: #000000; padding: 4px 0; border: none; background: none;';
            group.appendChild(textDiv);

            // Style the group to remove any borders
            group.style.setProperty('border', 'none', 'important');
          }
        });
      });

      const filterRows = clonedDoc.querySelectorAll('.ci-filter-summary-row');
      filterRows.forEach((row) => {
        row.style.setProperty('flex-wrap', 'wrap', 'important');
        row.style.setProperty('min-width', '0', 'important');
      });

      const filterValues = clonedDoc.querySelectorAll('.ci-filter-summary-value');
      filterValues.forEach((value) => {
        value.style.setProperty('word-wrap', 'break-word', 'important');
        value.style.setProperty('overflow-wrap', 'break-word', 'important');
        value.style.setProperty('white-space', 'normal', 'important');
        value.style.setProperty('max-width', '100%', 'important');
        value.style.setProperty('color', '#000000', 'important');
      });

      const panels = clonedDoc.querySelectorAll('.ci-panel');
      panels.forEach((panel) => {
        panel.style.setProperty('height', 'auto', 'important');
        panel.style.setProperty('min-height', 'auto', 'important');
        panel.style.setProperty('max-height', 'none', 'important');
      });

      const tableWraps = clonedDoc.querySelectorAll(
        '.ci-table-wrap, .ci-comment-panel-content, .ci-rubric-panel-wrap, ' +
        '.ci-rubric-graph-wrap, .ci-modal-list, .ci-modal-chart, ' +
        '.ci-comment-bank-modal .ci-modal-list, .ci-sc-grid, ' +
        '.ci-short-answer-table, .ci-rubric-table, .ci-modal-table'
      );
      tableWraps.forEach((wrap) => {
        wrap.style.setProperty('overflow', 'visible', 'important');
        wrap.style.setProperty('overflow-x', 'visible', 'important');
        wrap.style.setProperty('overflow-y', 'visible', 'important');
        wrap.style.setProperty('max-height', 'none', 'important');
        wrap.style.setProperty('min-height', '0', 'important');
        wrap.style.setProperty('height', 'auto', 'important');
      });

      // Special handling for rubric graph scrollable containers
      const rubricScrolls = clonedDoc.querySelectorAll('.ci-rubric-graph-scroll');
      rubricScrolls.forEach((scroll) => {
        scroll.style.setProperty('overflow', 'visible', 'important');
        scroll.style.setProperty('overflow-x', 'visible', 'important');
        scroll.style.setProperty('overflow-y', 'visible', 'important');
        scroll.style.setProperty('width', 'max-content', 'important');
        scroll.style.setProperty('max-width', 'none', 'important');
      });

      // Expand rubric graph area and content to full width
      const rubricAreas = clonedDoc.querySelectorAll('.ci-rubric-graph-area');
      rubricAreas.forEach((area) => {
        area.style.setProperty('overflow', 'visible', 'important');
        area.style.setProperty('width', 'max-content', 'important');
        area.style.setProperty('max-width', 'none', 'important');
        area.style.setProperty('min-width', 'max-content', 'important');
      });

      // Expand rubric graph content and container
      const rubricContents = clonedDoc.querySelectorAll('.ci-rubric-graph-content, .ci-rubric-graph-container');
      rubricContents.forEach((content) => {
        content.style.setProperty('overflow', 'visible', 'important');
        content.style.setProperty('flex', 'none', 'important');
        content.style.setProperty('width', 'max-content', 'important');
        content.style.setProperty('max-width', 'none', 'important');
        content.style.setProperty('min-width', 'max-content', 'important');
      });

      // Ensure all rubric task sections maintain their width
      const rubricSections = clonedDoc.querySelectorAll('.ci-rubric-task-section');
      rubricSections.forEach((section) => {
        section.style.setProperty('flex-shrink', '0', 'important');
        section.style.setProperty('flex-grow', '0', 'important');
      });

      // Expand task headers and labels
      const rubricHeaders = clonedDoc.querySelectorAll('.ci-rubric-task-headers, .ci-rubric-criteria-labels-row');
      rubricHeaders.forEach((header) => {
        header.style.setProperty('overflow', 'visible', 'important');
        header.style.setProperty('width', 'max-content', 'important');
        header.style.setProperty('max-width', 'none', 'important');
        header.style.setProperty('min-width', 'max-content', 'important');
        header.style.setProperty('flex', 'none', 'important');
      });

      const allTables = clonedDoc.querySelectorAll('table');
      allTables.forEach((table) => {
        table.style.setProperty('height', 'auto', 'important');
        table.style.setProperty('min-height', '0', 'important');
        table.style.setProperty('max-height', 'none', 'important');
        table.style.setProperty('overflow', 'visible', 'important');

        const tbody = table.querySelector('tbody');
        if (tbody) {
          tbody.style.setProperty('max-height', 'none', 'important');
          tbody.style.setProperty('overflow', 'visible', 'important');
          tbody.style.setProperty('height', 'auto', 'important');
          tbody.style.setProperty('display', 'table-row-group', 'important');
        }

        const rows = table.querySelectorAll('tr');
        rows.forEach(row => {
          row.style.setProperty('display', 'table-row', 'important');
          row.style.setProperty('height', 'auto', 'important');
        });

        const cells = table.querySelectorAll('td, th');
        cells.forEach(cell => {
          cell.style.setProperty('display', 'table-cell', 'important');
          cell.style.setProperty('overflow', 'visible', 'important');
        });
      });

      const styleTags = clonedDoc.querySelectorAll('style');
      styleTags.forEach((styleTag) => {
        const originalCss = styleTag.textContent || '';
        const sanitizedCss = sanitizeUnsupportedColorFunctions(originalCss);
        if (sanitizedCss !== originalCss) {
          styleTag.textContent = sanitizedCss;
        }
      });

      const styledElements = clonedDoc.querySelectorAll('[style]');
      styledElements.forEach((el) => {
        const inlineStyle = el.getAttribute('style');
        if (!inlineStyle) return;
        const sanitizedStyle = sanitizeUnsupportedColorFunctions(inlineStyle);
        if (sanitizedStyle !== inlineStyle) {
          el.setAttribute('style', sanitizedStyle);
        }
      });

      try {
        Array.from(clonedDoc.styleSheets).forEach((sheet) => {
          try {
            if (!sheet.cssRules) return;

            Array.from(sheet.cssRules).forEach((rule) => {
              if (rule.style) {
                Array.from(rule.style).forEach((prop) => {
                  const value = rule.style.getPropertyValue(prop);
                  if (hasUnsupportedColorFunction(value)) {
                    const sanitized = sanitizeUnsupportedColorFunctions(value);
                    rule.style.setProperty(prop, sanitized, rule.style.getPropertyPriority(prop));
                  }
                });
              }
            });
          } catch (e) {
          }
        });
      } catch (e) {
      }

      if (!clonedWindow) return;

      const colorProps = [
        ['color', '#111111'],
        ['background-color', 'transparent'],
        ['border-color', 'rgba(0, 0, 0, 0.2)'],
        ['border-top-color', 'rgba(0, 0, 0, 0.2)'],
        ['border-right-color', 'rgba(0, 0, 0, 0.2)'],
        ['border-bottom-color', 'rgba(0, 0, 0, 0.2)'],
        ['border-left-color', 'rgba(0, 0, 0, 0.2)'],
        ['outline-color', 'rgba(0, 0, 0, 0.3)'],
        ['text-decoration-color', 'currentColor'],
        ['caret-color', 'auto'],
        ['fill', 'currentColor'],
        ['stroke', 'currentColor'],
      ];

      const allElements = clonedDoc.querySelectorAll('*');
      allElements.forEach((el) => {
        const computed = clonedWindow.getComputedStyle(el);

        colorProps.forEach(([prop, fallback]) => {
          const value = computed.getPropertyValue(prop);
          if (hasUnsupportedColorFunction(value)) {
            el.style.setProperty(prop, fallback, 'important');
          }
        });

        const bgImage = computed.getPropertyValue('background-image');
        if (hasUnsupportedColorFunction(bgImage)) {
          el.style.setProperty('background-image', 'none', 'important');
        }

        const boxShadow = computed.getPropertyValue('box-shadow');
        if (hasUnsupportedColorFunction(boxShadow)) {
          el.style.setProperty('box-shadow', 'none', 'important');
        }

        const textShadow = computed.getPropertyValue('text-shadow');
        if (hasUnsupportedColorFunction(textShadow)) {
          el.style.setProperty('text-shadow', 'none', 'important');
        }

        const inlineStyle = el.getAttribute('style') || '';
        const cssVarMatches = inlineStyle.matchAll(/--[^:]+:\s*([^;]+)/g);
        for (const match of cssVarMatches) {
          const varValue = match[1];
          if (hasUnsupportedColorFunction(varValue)) {
            const sanitizedValue = sanitizeUnsupportedColorFunctions(varValue);
            const varName = match[0].split(':')[0].trim();
            el.style.setProperty(varName, sanitizedValue);
          }
        }
      });

      const allElementsInModal = clonedDoc.querySelectorAll('[data-section*="-modal"] *');
      allElementsInModal.forEach((el) => {
        const computed = clonedWindow.getComputedStyle(el);
        const maxHeight = computed.getPropertyValue('max-height');
        const overflow = computed.getPropertyValue('overflow');
        const overflowY = computed.getPropertyValue('overflow-y');

        if (maxHeight && maxHeight !== 'none' && maxHeight !== '0px') {
          el.style.setProperty('max-height', 'none', 'important');
        }
        if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
          el.style.setProperty('overflow', 'visible', 'important');
          el.style.setProperty('overflow-y', 'visible', 'important');
          el.style.setProperty('overflow-x', 'visible', 'important');
        }
      });

      const modalTextElements = clonedDoc.querySelectorAll(
        '.ci-modal-item-name, .ci-modal-item-percentage, .ci-modal-table td, .ci-modal-table th, ' +
        '.ci-table-student, .ci-sc-item-text, .ci-panel-header-title, ' +
        'th, td, .ci-rubric-tab, .ci-modal-sort span, .ci-modal-class-avg'
      );
      modalTextElements.forEach((el) => {
        const tagName = el.tagName.toLowerCase();
        if (tagName === 'th' || el.classList.contains('ci-panel-header-title')) {
          el.style.setProperty('color', '#000000', 'important');
          el.style.setProperty('font-weight', '600', 'important');
        } else {
          el.style.setProperty('color', '#000000', 'important');
        }
      });

      const donutCharts = clonedDoc.querySelectorAll('.ci-donut-chart, .ci-half-donut');
      donutCharts.forEach((chart) => {
        const existingOverlay = chart.querySelector('.ci-export-donut-overlay');
        if (existingOverlay) {
          existingOverlay.remove();
        }

        if (chart.classList.contains('ci-donut-chart--empty')) {
          chart.style.setProperty('background', '#e5e2e8', 'important');
          return;
        }

        const classworkPercentRaw = clonedWindow
          .getComputedStyle(chart)
          .getPropertyValue('--classwork-percent')
          .trim();
        let classworkRatio = null;

        if (classworkPercentRaw) {
          const semicirclePercent = Number.parseFloat(classworkPercentRaw);
          if (Number.isFinite(semicirclePercent)) {
            classworkRatio = Math.max(0, Math.min(1, semicirclePercent / 50));
          }
        }

        if (classworkRatio == null) {
          const cardWrap = chart.closest('.ci-total-submissions-wrap');
          const leftCountText = cardWrap?.querySelector('.ci-total-label-left strong')?.textContent || '';
          const rightCountText = cardWrap?.querySelector('.ci-total-label-right strong')?.textContent || '';
          const classworkCount = Number((leftCountText.match(/\d+/) || ['0'])[0]);
          const independentCount = Number((rightCountText.match(/\d+/) || ['0'])[0]);
          const totalCount = classworkCount + independentCount;
          classworkRatio = totalCount > 0 ? classworkCount / totalCount : 0.5;
        }

        const width = Math.max(100, Math.round(chart.clientWidth || 132));
        const height = Math.max(50, Math.round(chart.clientHeight || 66));
        const radius = Math.max(20, Math.min(width * 0.38, height * 0.82));
        const centerX = width / 2;
        const centerY = height;
        const strokeWidth = Math.max(8, Math.round(radius * 0.26));

        const overlay = clonedDoc.createElement('canvas');
        overlay.className = 'ci-export-donut-overlay';
        overlay.width = width;
        overlay.height = height;
        overlay.style.position = 'absolute';
        overlay.style.left = '0';
        overlay.style.top = '0';
        overlay.style.width = `${width}px`;
        overlay.style.height = `${height}px`;
        overlay.style.pointerEvents = 'none';
        overlay.style.zIndex = '1';

        const ctx = overlay.getContext('2d');
        if (ctx) {
          ctx.lineCap = 'round';
          ctx.lineWidth = strokeWidth;

          const start = Math.PI;
          const mid = Math.PI + (Math.PI * classworkRatio);
          const end = 2 * Math.PI;

          ctx.strokeStyle = '#e5e2e8';
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius, start, end, false);
          ctx.stroke();

          ctx.strokeStyle = '#6e4bde';
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius, start, mid, false);
          ctx.stroke();

          ctx.strokeStyle = '#1ade54';
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius, mid, end, false);
          ctx.stroke();
        }

        chart.style.setProperty('position', 'relative', 'important');
        chart.style.setProperty('background', 'transparent', 'important');
        chart.style.setProperty('overflow', 'hidden', 'important');
        const chartNumber = chart.querySelector('.ci-donut-chart-number, .ci-half-donut-number');
        if (chartNumber) {
          chartNumber.style.setProperty('z-index', '2', 'important');
        }
        chart.appendChild(overlay);
      });
    },
  };

  try {
    const canvas = await html2canvas(element, { ...defaultOptions, ...options });
    return canvas;
  } catch (error) {
    console.error('html2canvas capture failed:', error);
    throw error;
  }
}


export async function captureDashboard() {
  const dashboardElement = document.querySelector('.ci-page.class-insights-page');

  if (!dashboardElement) {
    throw new Error('Dashboard element not found');
  }

  return captureElement(dashboardElement, {
    windowWidth: dashboardElement.scrollWidth,
    windowHeight: dashboardElement.scrollHeight,
  });
}


export async function captureSection(sectionId) {
  const sectionElement = document.querySelector(`[data-section="${sectionId}"]`);

  if (!sectionElement) {
    throw new Error(`Section element with data-section="${sectionId}" not found`);
  }

  const isModal = sectionId.includes('-modal');
  if (isModal) {
    const cloneHost = document.createElement('div');
    cloneHost.style.position = 'fixed';
    cloneHost.style.left = '-20000px';
    cloneHost.style.top = '0';
    cloneHost.style.pointerEvents = 'none';
    cloneHost.style.opacity = '1';
    cloneHost.style.background = '#ffffff';
    cloneHost.style.width = `${Math.max(sectionElement.scrollWidth, 1400)}px`;
    cloneHost.style.maxWidth = 'none';
    cloneHost.style.overflow = 'visible';

    const clonedSection = sectionElement.cloneNode(true);
    clonedSection.setAttribute('data-ci-export-clone', 'true');
    clonedSection.style.maxHeight = 'none';
    clonedSection.style.minHeight = '0';
    clonedSection.style.height = 'auto';
    clonedSection.style.overflow = 'visible';
    clonedSection.style.overflowY = 'visible';
    clonedSection.style.overflowX = 'visible';
    clonedSection.style.setProperty('visibility', 'visible', 'important');
    clonedSection.style.setProperty('opacity', '1', 'important');
    clonedSection.style.setProperty('pointer-events', 'none', 'important');
    clonedSection.style.width = '100%';
    clonedSection.style.maxWidth = 'none';
    clonedSection.style.display = 'flex';
    clonedSection.style.flexDirection = 'column';

    cloneHost.appendChild(clonedSection);
    document.body.appendChild(cloneHost);

    const sourceCanvases = sectionElement.querySelectorAll('canvas');
    const clonedCanvases = clonedSection.querySelectorAll('canvas');
    sourceCanvases.forEach((sourceCanvas, index) => {
      const targetCanvas = clonedCanvases[index];
      if (!targetCanvas) return;
      const sourceWidth = sourceCanvas.width || Math.round(sourceCanvas.clientWidth || 0);
      const sourceHeight = sourceCanvas.height || Math.round(sourceCanvas.clientHeight || 0);
      if (!sourceWidth || !sourceHeight) return;

      targetCanvas.width = sourceWidth;
      targetCanvas.height = sourceHeight;
      targetCanvas.style.width = `${Math.round(sourceCanvas.clientWidth || sourceWidth)}px`;
      targetCanvas.style.height = `${Math.round(sourceCanvas.clientHeight || sourceHeight)}px`;
      targetCanvas.style.maxWidth = 'none';
      targetCanvas.style.maxHeight = 'none';

      const ctx = targetCanvas.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, sourceWidth, sourceHeight);
      ctx.drawImage(sourceCanvas, 0, 0, sourceWidth, sourceHeight);
    });

    const expandableElements = clonedSection.querySelectorAll(
      '.ci-modal-header, .ci-modal-filters, .ci-modal-table-controls, .ci-modal-content, ' +
      '.ci-modal-table-content, .ci-modal-list, .ci-table-wrap, .ci-rubric-panel-wrap, ' +
      '.ci-rubric-view-control, .ci-rubric-graph-wrap, .ci-rubric-graph-area, ' +
      '.ci-rubric-graph-container, .ci-modal-chart, .ci-sc-grid, .ci-sc-card, table, thead, tbody, tr'
    );
    expandableElements.forEach((el) => {
      el.style.maxHeight = 'none';
      el.style.minHeight = '0';
      el.style.height = 'auto';
      el.style.overflow = 'visible';
      el.style.overflowY = 'visible';
      el.style.overflowX = 'visible';
      el.style.flex = 'none';

      // Special width handling for rubric graph elements
      if (el.classList.contains('ci-rubric-graph-wrap') ||
          el.classList.contains('ci-rubric-graph-area') ||
          el.classList.contains('ci-rubric-graph-container') ||
          el.classList.contains('ci-rubric-graph-scroll') ||
          el.classList.contains('ci-rubric-graph-content') ||
          el.classList.contains('ci-rubric-task-headers') ||
          el.classList.contains('ci-rubric-criteria-labels-row')) {
        el.style.width = 'max-content';
        el.style.maxWidth = 'none';
        el.style.minWidth = 'max-content';
      } else if (el.classList.contains('ci-sc-grid')) {
        el.style.width = '100%';
      }
    });

    // Ensure rubric graph scroll container expands
    const rubricScrollContainers = clonedSection.querySelectorAll('.ci-rubric-graph-scroll');
    rubricScrollContainers.forEach((scroll) => {
      scroll.style.overflow = 'visible';
      scroll.style.overflowX = 'visible';
      scroll.style.width = 'max-content';
      scroll.style.maxWidth = 'none';
    });

    // Ensure all rubric sections don't shrink
    const rubricTaskSections = clonedSection.querySelectorAll('.ci-rubric-task-section');
    rubricTaskSections.forEach((section) => {
      section.style.flexShrink = '0';
      section.style.flexGrow = '0';
    });

    if (sectionId === 'comment-banks-modal') {
      const modalContent = clonedSection.querySelector('.ci-modal-content');
      const modalList = clonedSection.querySelector('.ci-modal-list');
      const modalChart = clonedSection.querySelector('.ci-modal-chart');
      if (modalContent) {
        modalContent.style.setProperty('display', 'grid', 'important');
        modalContent.style.setProperty('grid-template-columns', '1fr 1fr', 'important');
        modalContent.style.setProperty('align-items', 'stretch', 'important');
      }
      if (modalList) {
        modalList.style.setProperty('overflow', 'visible', 'important');
      }
      if (modalChart) {
        modalChart.style.setProperty('display', 'flex', 'important');
        modalChart.style.setProperty('align-items', 'center', 'important');
        modalChart.style.setProperty('justify-content', 'center', 'important');
        modalChart.style.setProperty('min-height', '420px', 'important');
      }
      const chartCanvases = clonedSection.querySelectorAll('.ci-modal-chart canvas');
      chartCanvases.forEach((canvas) => {
        canvas.style.setProperty('max-width', 'none', 'important');
        canvas.style.setProperty('max-height', 'none', 'important');
      });
    }

    const stickyElements = clonedSection.querySelectorAll('.ci-sticky-col, .ci-sticky-right');
    stickyElements.forEach((el) => {
      el.style.position = 'static';
      el.style.left = 'auto';
      el.style.right = 'auto';
      el.style.zIndex = 'auto';
    });

    const clonedAllElements = clonedSection.querySelectorAll('*');
    clonedAllElements.forEach((el) => {
      const computed = window.getComputedStyle(el);

      if (computed.maxHeight && computed.maxHeight !== 'none' && computed.maxHeight !== '0px') {
        el.style.setProperty('max-height', 'none', 'important');
      }
      if (computed.height && computed.height !== 'auto') {
        el.style.setProperty('height', 'auto', 'important');
      }

      if (computed.overflowY === 'auto' || computed.overflowY === 'scroll' || computed.overflowY === 'hidden') {
        el.style.setProperty('overflow-y', 'visible', 'important');
      }
      if (computed.overflowX === 'auto' || computed.overflowX === 'scroll' || computed.overflowX === 'hidden') {
        el.style.setProperty('overflow-x', 'visible', 'important');
      }
      if (computed.overflow === 'hidden' || computed.overflow === 'auto' || computed.overflow === 'scroll') {
        el.style.setProperty('overflow', 'visible', 'important');
      }

      if (computed.flex && computed.flex !== 'none') {
        el.style.setProperty('flex', 'none', 'important');
      }

      if (computed.display === 'none') {
        el.style.setProperty('display', 'block', 'important');
      }
    });

    clonedSection.offsetHeight;

    await new Promise(resolve => setTimeout(resolve, 800));


    try {
      const captureWidth = Math.max(clonedSection.scrollWidth, 1400);
      const captureHeight = Math.max(clonedSection.scrollHeight, clonedSection.clientHeight, 1200);

      const MAX_SAFE_HEIGHT = 8000; // Safe height per chunk to avoid quality loss

      if (captureHeight > MAX_SAFE_HEIGHT) {
        const canvas = await captureInChunks(clonedSection, captureWidth, captureHeight, MAX_SAFE_HEIGHT);
        if (cloneHost.parentNode) {
          cloneHost.parentNode.removeChild(cloneHost);
        }
        return canvas;
      }

      const safeScale = 3; // Higher scale for better quality

      const canvas = await captureElement(clonedSection, {
        scale: safeScale,
        windowWidth: captureWidth,
        windowHeight: captureHeight,
        scrollX: 0,
        scrollY: 0,
      });
      if (cloneHost.parentNode) {
        cloneHost.parentNode.removeChild(cloneHost);
      }
      return canvas;
    } catch (error) {
      if (cloneHost.parentNode) {
        cloneHost.parentNode.removeChild(cloneHost);
      }
      throw error;
    }
  }

  return captureElement(sectionElement);
}

async function captureInChunks(element, totalWidth, totalHeight, maxChunkHeight) {
  const SCALE = 3; // High quality scale
  const numChunks = Math.ceil(totalHeight / maxChunkHeight);
  const chunks = [];


  for (let i = 0; i < numChunks; i++) {
    const startY = i * maxChunkHeight;
    const chunkHeight = Math.min(maxChunkHeight, totalHeight - startY);


    const chunkWrapper = document.createElement('div');
    chunkWrapper.style.position = 'fixed';
    chunkWrapper.style.left = '-20000px';
    chunkWrapper.style.top = '0';
    chunkWrapper.style.width = `${totalWidth}px`;
    chunkWrapper.style.height = `${chunkHeight}px`;
    chunkWrapper.style.overflow = 'hidden';
    chunkWrapper.style.background = '#ffffff';

    const chunkContent = element.cloneNode(true);
    chunkContent.style.position = 'absolute';
    chunkContent.style.top = `-${startY}px`;
    chunkContent.style.left = '0';
    chunkContent.style.width = `${totalWidth}px`;

    chunkWrapper.appendChild(chunkContent);
    document.body.appendChild(chunkWrapper);

    await new Promise(resolve => setTimeout(resolve, 150));

    const chunkCanvas = await captureElement(chunkWrapper, {
      scale: SCALE,
      windowWidth: totalWidth,
      windowHeight: chunkHeight,
      scrollX: 0,
      scrollY: 0,
    });

    chunks.push(chunkCanvas);

    document.body.removeChild(chunkWrapper);
  }

  const finalCanvas = document.createElement('canvas');
  finalCanvas.width = totalWidth * SCALE; // Account for scale
  finalCanvas.height = totalHeight * SCALE;
  const ctx = finalCanvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context for stitching');
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);

  let currentY = 0;
  for (const chunk of chunks) {
    ctx.drawImage(chunk, 0, currentY);
    currentY += chunk.height;
  }

  return finalCanvas;
}


export async function captureKpiCards() {
  const kpiElement = document.querySelector('.ci-kpi-grid');

  if (!kpiElement) {
    throw new Error('KPI summary cards element not found');
  }

  return captureElement(kpiElement);
}


export async function capturePanelsRow() {
  const panelsElement = document.querySelector('.ci-panels-grid');

  if (!panelsElement) {
    throw new Error('Combined panels row element not found');
  }

  return captureElement(panelsElement);
}

export async function captureAllPanelGrids() {
  const panelsElements = document.querySelectorAll('.ci-panels-grid');

  if (!panelsElements || panelsElements.length === 0) {
    throw new Error('No panel grid elements found');
  }

  const canvases = [];
  for (const panelElement of panelsElements) {
    const canvas = await captureElement(panelElement);
    canvases.push(canvas);
  }

  return canvases;
}


export async function captureFullDashboardSections() {
  const sections = [];

  try {
    const kpiElement = document.querySelector('.ci-kpi-grid');
    if (kpiElement) {
      sections.push(await captureElement(kpiElement));
    }
  } catch (error) {
    console.warn('Failed to capture KPI cards:', error);
  }

  const panelGrids = document.querySelectorAll('.ci-panels-grid');
  for (const grid of panelGrids) {
    try {
      sections.push(await captureElement(grid));
    } catch (error) {
      console.warn('Failed to capture panel grid:', error);
    }
  }

  return sections;
}
